import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  Drawer,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import { StandaloneMarkdown } from './assistant-ui/markdown-text';

/**
 * AIAssistantDrawer - A reusable AI-powered assistant drawer component
 *
 * Uses the /cme endpoint internally for all AI interactions, with optional file upload support.
 *
 * @param {boolean} open - Whether the drawer is open
 * @param {function} onClose - Callback when drawer is closed
 * @param {string} title - Title shown in the drawer header
 * @param {string} subtitle - Subtitle/description shown below title
 * @param {string} initialMessage - The first message from the AI assistant
 * @param {string} systemPrompt - System prompt to guide AI behavior
 * @param {object} contextData - Optional data object to send as JSON file attachment on first message (client-side)
 * @param {string} contextFileName - Name for the context file (default: "context.json")
 * @param {object} contextSource - Optional server-side data source (e.g., { type: "export_threads", filters: {...} })
 * @param {function} onUseResult - Callback when user clicks "Use This" button, receives the generated result
 * @param {string} resultLabel - Label for the result section (default: "Generated Result")
 * @param {string} useButtonText - Text for the use button (default: "Use This")
 */
export default function AIAssistantDrawer({
  open,
  onClose,
  title = 'AI Assistant',
  subtitle = '',
  initialMessage = "Hi! I'm here to help. What would you like to create?",
  systemPrompt = '',
  contextData = null,
  contextFileName = 'context.json',
  contextSource = null,  // Server-side data source
  onUseResult,
  resultLabel = 'Generated Result',
  useButtonText = 'Use This',
}) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: initialMessage }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [contextSent, setContextSent] = useState(false);
  const [assistantSession, setAssistantSession] = useState(null);  // For multi-turn with file reuse
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getAuthToken = () => localStorage.getItem('cme_token');

  // Reset state when drawer opens
  useEffect(() => {
    if (open) {
      setMessages([{ role: 'assistant', content: initialMessage }]);
      setGeneratedResult(null);
      setInput('');
      setContextSent(false);
      setAssistantSession(null);
    }
  }, [open, initialMessage]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');

    // Add user message to UI
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = getAuthToken();

      // Build conversation history for the API
      const conversationMessages = messages
        .filter(msg => msg.role !== 'assistant' || msg.content !== initialMessage) // Exclude initial greeting
        .map(msg => ({ role: msg.role, content: msg.content }));

      // Add current user message
      conversationMessages.push({ role: 'user', content: userMessage });

      // Create FormData for /api/chat/complete endpoint
      const formData = new FormData();
      const payload = {
        messages: conversationMessages,
        system_prompt: systemPrompt || '',
        temperature: 0.7,
        max_tokens: 16000
      };

      // Include server-side context source on first message only
      if (!contextSent && contextSource) {
        payload.context_source = contextSource;
        console.log('[AIAssistant] Using server-side context source:', contextSource.type);
        setContextSent(true);
      }

      // Include assistant session for follow-up messages (file reuse)
      if (assistantSession) {
        payload.assistant_session = assistantSession;
        console.log('[AIAssistant] Continuing session:', assistantSession.provider);
      }

      formData.append('payload', JSON.stringify(payload));

      // Attach context data as JSON file on first message only (client-side fallback)
      if (!contextSent && contextData && !contextSource) {
        const jsonContent = JSON.stringify(contextData, null, 2);
        const jsonBlob = new Blob([jsonContent], { type: 'application/json' });
        const jsonFile = new File([jsonBlob], contextFileName, { type: 'application/json' });
        formData.append('files', jsonFile);
        console.log('[AIAssistant] Attaching context file:', contextFileName, 'size:', jsonContent.length, 'bytes');
        setContextSent(true);
      }

      console.log('[AIAssistant] Sending request to /api/assistant with', conversationMessages.length, 'messages');
      const response = await fetch('/api/assistant', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      // Check if response is streaming (SSE) or JSON
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('text/event-stream')) {
        // Handle streaming response (OpenAI Assistants API)
        console.log('[AIAssistant] Streaming response detected');

        // Add empty assistant message that we'll update
        setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                if (data.type === 'text' && data.text) {
                  fullResponse += data.text;
                  // Update the last message with accumulated text
                  setMessages(prev => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: 'assistant', content: fullResponse };
                    return updated;
                  });
                } else if (data.type === 'session' && data.session) {
                  setAssistantSession(data.session);
                  console.log('[AIAssistant] Session stored:', data.session.provider);
                } else if (data.type === 'done') {
                  console.log('[AIAssistant] Stream complete, usage:', data.usage);
                }
              } catch (e) {
                // Ignore parse errors for incomplete chunks
              }
            }
          }
        }

        // Check for result marker in final response
        let extractedResult = null;
        if (fullResponse.includes('```result')) {
          const match = fullResponse.match(/```result\s*([\s\S]*?)\s*```/);
          if (match) {
            extractedResult = match[1].trim();
          }
        }
        if (extractedResult) {
          setGeneratedResult(extractedResult);
        }

      } else {
        // Handle JSON response (non-OpenAI providers like Gemini)
        const result = await response.json();
        const fullResponse = result.content || '';

        // Store assistant session for follow-up messages (file reuse)
        if (result.assistant_session) {
          setAssistantSession(result.assistant_session);
          console.log('[AIAssistant] Session stored:', result.assistant_session.provider);
        }

        // Check if response contains a result marker (```result ... ```)
        let extractedResult = null;
        if (fullResponse.includes('```result')) {
          const match = fullResponse.match(/```result\s*([\s\S]*?)\s*```/);
          if (match) {
            extractedResult = match[1].trim();
          }
        }

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: fullResponse || 'I received your message. How can I help further?'
        }]);

        if (extractedResult) {
          setGeneratedResult(extractedResult);
        }
      }

    } catch (error) {
      console.error('AI Assistant API error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
      // Refocus the input after sending
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleUseResult = () => {
    if (generatedResult && onUseResult) {
      onUseResult(generatedResult);
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 10,
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 550, md: 650 },
          maxWidth: '100%',
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(156, 39, 176, 0.15) 0%, rgba(33, 150, 243, 0.15) 100%)'
              : 'linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(33, 150, 243, 0.08) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {messages.map((msg, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  maxWidth: msg.role === 'user' ? '85%' : '95%',
                  borderRadius: 2,
                  bgcolor: msg.role === 'user'
                    ? 'primary.main'
                    : (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {msg.role === 'assistant' ? (
                  <Box sx={{
                    '& .aui-md': { fontSize: '0.875rem' },
                    '& .aui-md-p': { mt: 2, mb: 2 },
                    '& pre': {
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
                      p: 1.5,
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.8rem',
                    },
                    '& code': {
                      fontSize: '0.8rem',
                    },
                    '& ul, & ol': {
                      pl: 2.5,
                      my: 1,
                    },
                    '& li': {
                      mb: 0.5,
                    },
                  }}>
                    <StandaloneMarkdown text={msg.content} />
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </Typography>
                )}
              </Paper>
            </Box>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Thinking...
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Generated Result Preview */}
        {generatedResult && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'rgba(76, 175, 80, 0.05)',
                borderColor: 'success.main',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="success.main" fontWeight={600}>
                  {resultLabel}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={handleUseResult}
                >
                  {useButtonText}
                </Button>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                }}
              >
                {generatedResult}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            multiline
            maxRows={3}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!input.trim() || isLoading}
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
              }
            }}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
